import { memo } from 'react';
import { cn } from '@cyphex/ui';

interface QuickReactionsProps {
  /** The caller's three favourites, from `/me/preferences`. */
  emoji: readonly string[];
  /** Emoji already on this message — rendered pressed so a tap un-reacts. */
  reacted: ReadonlySet<string>;
  onReact: (emoji: string) => void;
  className?: string;
}

/**
 * The three one-tap reactions in the hover toolbar.
 *
 * Plain buttons rather than `ReactionPill`s: a pill carries a count and implies
 * the reaction already exists. These are an ACTION, so they stay unlabelled and
 * only change state to show you have already used one.
 */
export const QuickReactions = memo(function QuickReactions({
  emoji,
  reacted,
  onReact,
  className,
}: QuickReactionsProps) {
  return (
    <div className={cn('flex items-center gap-0.5', className)}>
      {emoji.map((value) => {
        const isOn = reacted.has(value);
        return (
          <button
            key={value}
            type="button"
            aria-pressed={isOn}
            aria-label={isOn ? `Remove ${value} reaction` : `React with ${value}`}
            onClick={(event) => {
              onReact(value);
              /*
               * THE TOOLBAR HIDES ON `group-focus-within`, NOT JUST HOVER
               * (MessageActionsBar), so a clicked button keeps focus and pins
               * the whole bar open over the message after the pointer has long
               * gone. That is the "reaction box won't go away" — the bar was
               * never stuck, it was still focused.
               *
               * `detail > 0` means a real pointer click; a keyboard-activated
               * click reports 0. So the mouse releases focus and the bar
               * closes, while a keyboard user keeps it — which is the only way
               * they can reach the rest of the actions.
               */
              if (event.detail > 0) event.currentTarget.blur();
            }}
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-md text-md leading-none transition-colors duration-fast',
              isOn ? 'bg-primary-subtle' : 'hover:bg-subtle',
            )}
          >
            <span aria-hidden>{value}</span>
          </button>
        );
      })}
    </div>
  );
});
